import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../lib/useSubscription';
import Sidebar from './Sidebar';
import LoadingSpinner from './LoadingSpinner';

const GREEK_MONTHS = [
  'Ιανουαρίου', 'Φεβρουαρίου', 'Μαρτίου', 'Απριλίου',
  'Μαΐου', 'Ιουνίου', 'Ιουλίου', 'Αυγούστου',
  'Σεπτεμβρίου', 'Οκτωβρίου', 'Νοεμβρίου', 'Δεκεμβρίου'
];

const formatGreekDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return `${d.getDate()} ${GREEK_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const PLAN_ORDER = { free: 0, basic: 1, premium: 2, professional: 2, enterprise: 3, 'clinic max': 3 };

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, clinic, loading: authLoading } = useAuth();
  const [clinicId, setClinicId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  // Toast
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const toastTimeoutRef = useRef(null);

  // Profile state (Added required B2B Invoice fields)
  const [profileData, setProfileData] = useState({ 
    owner_first_name: '', 
    owner_last_name: '', 
    vat_number: '',
    company_name: '',
    activity: '',
    tax_office: '',
    address: ''
  });
  const [profileSaving, setProfileSaving] = useState(false);

  // Usage state
  const [usage, setUsage] = useState(null);
  const [limits, setLimits] = useState({ appointments: null, emails: null, users: null });

  // Cancel subscription state
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Change plan modal state
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [allPlans, setAllPlans] = useState([]);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [changingPlan, setChangingPlan] = useState(false);

  // Billing history state
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);

  // Scheduled plan change (downgrade)
  const [scheduledChange, setScheduledChange] = useState(null);

  // Derive clinicId
  useEffect(() => {
    if (clinic?.id) setClinicId(clinic.id);
  }, [clinic]);

  const { subscription, plan, status, loading: subLoading, refetch } = useSubscription(clinicId);

  // Check if subscription is expired (for banner)
  const isExpired = (() => {
    if (!status || !subscription) return false;
    if (!['cancelled', 'expired'].includes(status.name)) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = subscription.end_date ? new Date(subscription.end_date) : null;
    const trialEnd = subscription.trial_end_date ? new Date(subscription.trial_end_date) : null;
    if (endDate && today <= endDate) return false;
    if (trialEnd && today <= trialEnd) return false;
    return true;
  })();

  const isCancelled = status?.name === 'cancelled';

  // Effective access end date
  const accessEndDate = subscription?.end_date || subscription?.trial_end_date;

  // Load profile data from clinic
  useEffect(() => {
    if (clinic) {
      setProfileData({
        owner_first_name: clinic.owner_first_name || '',
        owner_last_name: clinic.owner_last_name || '',
        vat_number: clinic.vat_number || '',
        company_name: clinic.company_name || '',
        activity: clinic.activity || '',
        tax_office: clinic.tax_office || '',
        address: clinic.address || ''
      });
    }
  }, [clinic]);

  // Load usage data
  useEffect(() => {
    if (!clinicId) return;
    const fetchUsage = async () => {
      const { data: usageData } = await supabase
        .from('clinic_monthly_usage')
        .select('emails_sent, appointments_count')
        .eq('clinic_id', clinicId)
        .maybeSingle();

      setUsage(usageData || { emails_sent: 0, appointments_count: 0 });

      const [apptLimit, emailLimit, userLimit] = await Promise.all([
        supabase.rpc('check_clinic_limit', { p_clinic_id: clinicId, p_limit_type: 'appointments' }),
        supabase.rpc('check_clinic_limit', { p_clinic_id: clinicId, p_limit_type: 'emails' }),
        supabase.rpc('check_clinic_limit', { p_clinic_id: clinicId, p_limit_type: 'users' })
      ]);

      setLimits({
        appointments: apptLimit.data || { limit: null, current: 0 },
        emails: emailLimit.data || { limit: null, current: 0 },
        users: userLimit.data || { limit: null, current: 0 }
      });
    };
    fetchUsage();
  }, [clinicId]);

  // Fetch scheduled plan change when billing tab is active
  useEffect(() => {
    if (!clinicId || activeTab !== 'billing') return;
    const fetchScheduled = async () => {
      const { data } = await supabase
        .from('clinic_transactions')
        .select('*, plans(name)')
        .eq('clinic_id', clinicId)
        .eq('status', 'scheduled')
        .eq('type', 'plan_change')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      setScheduledChange(data || null);
    };
    fetchScheduled();
  }, [clinicId, activeTab, subscription]);

  // Fetch all plans when change plan modal opens
  useEffect(() => {
    if (!showChangePlanModal) return;
    const fetchPlans = async () => {
      const { data, error } = await supabase
        .from('plans')
        .select('*')
        .order('price', { ascending: true });
      if (!error && data) setAllPlans(data);
    };
    fetchPlans();
  }, [showChangePlanModal]);

  // Fetch billing history when history tab is opened
  useEffect(() => {
    if (activeTab !== 'history' || !clinicId) return;
    const fetchTransactions = async () => {
      setTransactionsLoading(true);
      const { data, error } = await supabase
        .from('clinic_transactions')
        .select('*, plans(name)')
        .eq('clinic_id', clinicId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (!error && data) setTransactions(data);
      setTransactionsLoading(false);
    };
    fetchTransactions();
  }, [activeTab, clinicId]);

  // Set billing period from subscription
  useEffect(() => {
    if (subscription?.billing_period) {
      setBillingPeriod(subscription.billing_period);
    }
  }, [subscription]);

  // Handle Stripe Checkout result from URL params
  useEffect(() => {
    const checkoutResult = searchParams.get('checkout');
    if (checkoutResult === 'success') {
      setActiveTab('billing');
      setSearchParams({}, { replace: true });

      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = setInterval(async () => {
        attempts++;
        await refetch();
        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          showToast('Η πληρωμή ολοκληρώθηκε επιτυχώς!');
        }
      }, 2000);

      return () => clearInterval(pollInterval);
    } else if (checkoutResult === 'cancel') {
      setActiveTab('billing');
      showToast('Η πληρωμή ακυρώθηκε', 'info');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  const showToast = (message, type = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, type });
    toastTimeoutRef.current = setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
  };

  // Save profile
  const handleSaveProfile = async () => {
    if (!clinicId) return;
    setProfileSaving(true);
    try {
      const { error } = await supabase
        .from('clinics')
        .update({
          owner_first_name: profileData.owner_first_name.trim() || null,
          owner_last_name: profileData.owner_last_name.trim() || null,
          vat_number: profileData.vat_number.trim() || null,
          /* TODO: Ξεσχολίασε τα παρακάτω ΜΟΝΟ αν έχεις προσθέσει αυτές τις στήλες στον πίνακα clinics στο Supabase 
          company_name: profileData.company_name.trim() || null,
          activity: profileData.activity.trim() || null,
          tax_office: profileData.tax_office.trim() || null,
          address: profileData.address.trim() || null
          */
        })
        .eq('id', clinicId);
      if (error) throw error;
      showToast('Τα στοιχεία αποθηκεύτηκαν επιτυχώς');
    } catch (err) {
      console.error('Error saving profile:', err);
      showToast('Σφάλμα κατά την αποθήκευση', 'error');
    } finally {
      setProfileSaving(false);
    }
  };

  // Cancel subscription
  const handleCancelSubscription = async () => {
    if (!subscription) return;
    setCancelling(true);
    try {
      if (subscription.stripe_subscription_id) {
        const { error: fnError } = await supabase.functions.invoke('cancel-subscription', {
          body: { clinic_id: clinicId }
        });
        if (fnError) console.warn('Stripe cancel failed, updating DB only:', fnError.message);
      }

      const { data: cancelledStatus, error: statusError } = await supabase
        .from('subscription_status')
        .select('id')
        .eq('name', 'cancelled')
        .single();
      if (statusError || !cancelledStatus) throw new Error('Could not find cancelled status');

      const { error: updateError } = await supabase
        .from('clinic_subscriptions')
        .update({ status_id: cancelledStatus.id })
        .eq('id', subscription.id);
      if (updateError) throw updateError;

      await supabase.from('clinic_transactions').insert({
        clinic_id: clinicId,
        type: 'cancellation',
        description: `Ακύρωση πλάνου ${plan?.name || ''}. Πρόσβαση μέχρι ${formatGreekDate(subscription.end_date)}.`,
        plan_id: subscription.plan_id,
        amount: 0,
        billing_period: subscription.billing_period || 'monthly'
      });

      await refetch();
      setShowCancelConfirm(false);
      showToast('Η συνδρομή σας ακυρώθηκε επιτυχώς');
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      showToast('Σφάλμα κατά την ακύρωση', 'error');
    } finally {
      setCancelling(false);
    }
  };

  // Change plan
  const handleChangePlan = async (newPlanId) => {
    if (!clinicId) return;
    const selectedPlan = allPlans.find(p => p.id === newPlanId);
    const changeType = getChangeType(selectedPlan);
    if (changeType === 'same') return;

    setChangingPlan(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          plan_id: newPlanId,
          billing_period: billingPeriod,
          clinic_id: clinicId,
          change_type: changeType
        }
      });
      if (error) throw error;

      if (data?.scheduled) {
        await refetch();
        setShowChangePlanModal(false);
        showToast(changeType === 'downgrade'
          ? `Η υποβάθμιση θα εφαρμοστεί στις ${formatGreekDate(subscription?.end_date)}`
          : 'Η αλλαγή πλάνου ενημερώθηκε επιτυχώς'
        );
        setChangingPlan(false);
        return;
      }

      if (!data?.url) throw new Error('No checkout URL returned');
      window.location.href = data.url;
    } catch (err) {
      console.error('Error creating checkout session:', err);
      showToast('Σφάλμα κατά τη δημιουργία πληρωμής', 'error');
      setChangingPlan(false);
    }
  };

  const getNextResetDate = () => {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return `1 ${GREEK_MONTHS[nextMonth.getMonth()]} ${nextMonth.getFullYear()}`;
  };

  const getAvailablePlans = () => {
    return allPlans.filter(p => p.id !== 'free');
  };

  const getChangeType = (planItem) => {
    if (!plan || !status) return 'new';
    const isInactive = ['cancelled', 'expired'].includes(status.name);
    if (isInactive) return 'reactivation';
    if (planItem.id === plan.id) {
      if (billingPeriod !== (subscription?.billing_period || 'monthly')) return 'period_change';
      return 'same';
    }
    const currentOrder = PLAN_ORDER[plan.name.toLowerCase()] ?? PLAN_ORDER[plan.id] ?? 0;
    const newOrder = PLAN_ORDER[planItem.name.toLowerCase()] ?? PLAN_ORDER[planItem.id] ?? 0;
    return newOrder > currentOrder ? 'upgrade' : 'downgrade';
  };

  const getPlanPrice = (planItem) => {
    const monthlyPrice = Number(planItem.price) || 0;
    if (billingPeriod === 'annual') {
      return (monthlyPrice * 10).toFixed(2); // 10 months = save 2
    }
    return monthlyPrice.toFixed(2);
  };

  const getPlanFeatures = (planItem) => {
    if (!planItem.features) return [];
    if (Array.isArray(planItem.features)) return planItem.features;
    try { return JSON.parse(planItem.features); } catch { return []; }
  };

  // Usage progress bar
  const UsageCard = ({ icon, label, current, limit }) => {
    const isUnlimited = limit === null || limit === undefined;
    const percentage = isUnlimited ? 0 : Math.min((current / limit) * 100, 100);
    const isNearLimit = !isUnlimited && percentage >= 80;
    const isAtLimit = !isUnlimited && percentage >= 100;

    return (
      <div className="rounded-xl p-5 border transition-all hover:border-gray-500" style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}>
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
            <span className="text-xl">{icon}</span>
          </div>
          <div>
            <p className="text-sm text-gray-400 font-medium">{label}</p>
            <p className="text-2xl font-bold text-white">
              {current}
              <span className="text-sm font-normal text-gray-500">
                {' / '}{isUnlimited ? 'Απεριόριστο' : limit}
              </span>
            </p>
          </div>
        </div>
        {!isUnlimited && (
          <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#374151' }}>
            <div
              className="h-full rounded-full transition-all duration-1000 ease-out"
              style={{
                width: `${percentage}%`,
                backgroundColor: isAtLimit ? '#EF4444' : isNearLimit ? '#F59E0B' : '#10B981'
              }}
            />
          </div>
        )}
        {isUnlimited && (
          <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(16, 185, 129, 0.15)' }}>
            <div className="h-full w-full" style={{ backgroundColor: 'rgba(16, 185, 129, 0.4)' }} />
          </div>
        )}
      </div>
    );
  };

  if (authLoading) return <LoadingSpinner />;

  if (!user) {
    navigate('/login');
    return null;
  }

  const tabs = [
    { key: 'profile', label: 'Στοιχεία Τιμολόγησης', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
    { key: 'billing', label: 'Χρεώσεις & Πλάνο', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
    { key: 'history', label: 'Ιστορικό Συναλλαγών', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
    { key: 'usage', label: 'Χρήση Πόρων', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' }
  ];

  const availablePlans = getAvailablePlans();

  return (
    <div className="flex flex-col lg:flex-row min-h-screen" style={{ backgroundColor: '#1A202C' }}>
      <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />

      <main className="flex-1 p-4 lg:p-8 overflow-y-auto">
        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <div className="rounded-xl overflow-hidden border p-4 lg:p-6" style={{ backgroundColor: '#111827', borderColor: '#374151', boxShadow: '0 10px 20px rgba(0,0,0,0.25)' }}>
            <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">Συνδρομή</h1>
            <p className="text-gray-400 text-base lg:text-lg">
              Διαχείριση πλάνου, στοιχείων τιμολόγησης και χρήσης
            </p>
          </div>
        </div>

        {/* Banners */}
        {isExpired && (
          <div className="mb-6 rounded-xl p-4 flex items-center space-x-3 border shadow-lg" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)' }}>
            <svg className="w-6 h-6 flex-shrink-0 text-red-400 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-red-400">Η συνδρομή σας έχει λήξει</p>
              <p className="text-sm text-gray-300 mt-0.5">Επιλέξτε ένα πλάνο για να συνεχίσετε να χρησιμοποιείτε την εφαρμογή χωρίς περιορισμούς.</p>
            </div>
          </div>
        )}

        {isCancelled && !isExpired && (
          <div className="mb-6 rounded-xl p-4 flex items-start space-x-3 border shadow-lg" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', borderColor: 'rgba(245, 158, 11, 0.3)' }}>
            <svg className="w-6 h-6 flex-shrink-0 mt-0.5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-amber-500">Η συνδρομή σας έχει ακυρωθεί</p>
              <p className="text-sm text-gray-300 mt-1">
                Η πρόσβαση στο πλάνο{plan?.name ? <span className="text-white font-bold"> {plan.name}</span> : ''} παραμένει ενεργή μέχρι τις{' '}
                <span className="text-white font-bold">{formatGreekDate(accessEndDate)}</span>. Δεν θα πραγματοποιηθεί καμία επιπλέον χρέωση.
              </p>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex space-x-2 mb-8 p-1.5 rounded-xl overflow-x-auto bg-[#1F2937] border border-gray-700">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center justify-center space-x-2 px-5 py-3 rounded-lg text-sm font-semibold transition-all whitespace-nowrap flex-1 ${
                activeTab === tab.key
                  ? 'bg-emerald-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
              </svg>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* ═══ Tab: Profile ═══ */}
        {activeTab === 'profile' && (
          <div className="rounded-xl overflow-hidden border shadow-lg" style={{ backgroundColor: '#111827', borderColor: '#374151' }}>
            <div className="px-6 py-5 border-b" style={{ borderColor: '#374151', background: 'linear-gradient(90deg, rgba(55,65,81,0.3), rgba(31,41,55,0.3))' }}>
              <h3 className="text-xl font-bold text-white">Προσωπικά & Εταιρικά Στοιχεία</h3>
              <p className="text-sm text-gray-400 mt-1">Συμπληρώστε τα στοιχεία της επιχείρησής σας για την ορθή έκδοση των τιμολογίων.</p>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Επωνυμία Επιχείρησης</label>
                  <input
                    type="text"
                    value={profileData.company_name}
                    onChange={(e) => setProfileData(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="π.χ. ΠΑΠΑΔΟΠΟΥΛΟΣ ΙΩΑΝΝΗΣ & ΣΙΑ Ε.Ε."
                    className="w-full px-4 py-3 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all border"
                    style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Αντικείμενο / Δραστηριότητα</label>
                  <input
                    type="text"
                    value={profileData.activity}
                    onChange={(e) => setProfileData(prev => ({ ...prev, activity: e.target.value }))}
                    placeholder="π.χ. Κτηνιατρική Κλινική"
                    className="w-full px-4 py-3 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all border"
                    style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Όνομα Υπευθύνου</label>
                  <input
                    type="text"
                    value={profileData.owner_first_name}
                    onChange={(e) => setProfileData(prev => ({ ...prev, owner_first_name: e.target.value }))}
                    placeholder="Εισάγετε όνομα"
                    className="w-full px-4 py-3 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all border"
                    style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Επώνυμο Υπευθύνου</label>
                  <input
                    type="text"
                    value={profileData.owner_last_name}
                    onChange={(e) => setProfileData(prev => ({ ...prev, owner_last_name: e.target.value }))}
                    placeholder="Εισάγετε επώνυμο"
                    className="w-full px-4 py-3 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all border"
                    style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">ΑΦΜ</label>
                  <input
                    type="text"
                    value={profileData.vat_number}
                    onChange={(e) => setProfileData(prev => ({ ...prev, vat_number: e.target.value }))}
                    placeholder="π.χ. 012345678"
                    className="w-full px-4 py-3 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all border font-mono"
                    style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Δ.Ο.Υ.</label>
                  <input
                    type="text"
                    value={profileData.tax_office}
                    onChange={(e) => setProfileData(prev => ({ ...prev, tax_office: e.target.value }))}
                    placeholder="π.χ. Α' Θεσσαλονίκης"
                    className="w-full px-4 py-3 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all border"
                    style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Διεύθυνση Έδρας</label>
                  <input
                    type="text"
                    value={profileData.address}
                    onChange={(e) => setProfileData(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Οδός, Αριθμός, Τ.Κ., Πόλη"
                    className="w-full px-4 py-3 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all border"
                    style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                  />
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-700 flex justify-end">
                <button
                  onClick={handleSaveProfile}
                  disabled={profileSaving}
                  className="w-full sm:w-auto px-8 py-3 rounded-xl font-bold text-white transition-all hover:scale-[1.02] hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}
                >
                  {profileSaving ? 'Αποθήκευση...' : 'Αποθήκευση Στοιχείων'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ Tab: Billing ═══ */}
        {activeTab === 'billing' && (
          <div className="space-y-6">
            {subLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : !subscription ? (
              <div className="rounded-xl border border-dashed border-gray-600 p-12 text-center" style={{ backgroundColor: '#111827' }}>
                <svg className="w-12 h-12 text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-gray-400 text-lg font-medium">Δεν βρέθηκε ενεργή συνδρομή.</p>
                <button onClick={() => setShowChangePlanModal(true)} className="mt-6 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors">
                  Επιλογή Πλάνου
                </button>
              </div>
            ) : (
              <div className="rounded-2xl border shadow-2xl relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.1) 0%, #111827 100%)', borderColor: 'rgba(16,185,129,0.3)' }}>
                {/* Decorative background glow */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none transform translate-x-1/2 -translate-y-1/2"></div>
                
                <div className="px-6 lg:px-8 py-6 border-b border-gray-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 relative z-10">
                  <div>
                    <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                      {plan?.name || 'Ενεργό Πλάνο'}
                      {status && (
                        <span
                          className="px-3 py-1 rounded-full text-xs font-bold tracking-wide"
                          style={{ backgroundColor: `${status.color}20`, color: status.color, border: `1px solid ${status.color}40` }}
                        >
                          {status.name === 'active' ? 'ΕΝΕΡΓΟ' :
                           status.name === 'trial' ? 'ΔΟΚΙΜΑΣΤΙΚΟ' :
                           status.name === 'expiring_soon' ? 'ΛΗΓΕΙ ΣΥΝΤΟΜΑ' :
                           status.name === 'expired' ? 'ΕΛΗΞΕ' :
                           status.name === 'cancelled' ? 'ΑΚΥΡΩΜΕΝΟ' : status.name}
                        </span>
                      )}
                    </h3>
                    <p className="text-gray-400 mt-1">{plan?.description || 'Οι δυνατότητες του πακέτου σας'}</p>
                  </div>
                </div>

                <div className="p-6 lg:p-8 relative z-10">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                    {/* Price */}
                    <div className="bg-[#1F2937]/50 rounded-xl p-4 border border-gray-800">
                      <p className="text-sm text-gray-400 mb-1 font-medium">Χρέωση</p>
                      <p className="text-3xl font-bold text-white flex items-baseline gap-1">
                        {plan?.price && Number(plan.price) > 0 ? (
                          <>
                            €{Number(plan.price).toFixed(2)}
                            <span className="text-sm font-medium text-gray-500">/{subscription.billing_period === 'annual' ? 'έτος' : 'μήνα'}</span>
                          </>
                        ) : (
                          <>€0.00<span className="text-sm font-medium text-gray-500">/μήνα</span></>
                        )}
                      </p>
                    </div>

                    {/* Duration */}
                    <div className="bg-[#1F2937]/50 rounded-xl p-4 border border-gray-800">
                      <p className="text-sm text-gray-400 mb-1 font-medium">Περίοδος Χρέωσης</p>
                      <p className="text-xl font-bold text-white flex items-center gap-2">
                        <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        {subscription.billing_period === 'annual' ? 'Ετήσια' : 'Μηνιαία'}
                      </p>
                    </div>

                    {/* Start Date */}
                    <div className="bg-[#1F2937]/50 rounded-xl p-4 border border-gray-800">
                      <p className="text-sm text-gray-400 mb-1 font-medium">Έναρξη</p>
                      <p className="text-lg font-semibold text-white">{formatGreekDate(subscription.start_date)}</p>
                    </div>

                    {/* End Date */}
                    <div className="bg-[#1F2937]/50 rounded-xl p-4 border border-gray-800">
                      <p className="text-sm text-gray-400 mb-1 font-medium">Λήξη / Ανανέωση</p>
                      <p className="text-lg font-semibold text-white">{formatGreekDate(subscription.end_date)}</p>
                    </div>
                  </div>

                  {/* Scheduled plan change notice */}
                  {scheduledChange && (
                    <div className="mt-8 rounded-xl p-5 flex items-start space-x-4 border shadow-md" style={{ backgroundColor: 'rgba(245, 158, 11, 0.05)', borderColor: 'rgba(245, 158, 11, 0.3)' }}>
                      <div className="p-2 bg-amber-500/20 rounded-full mt-0.5">
                        <svg className="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-base font-bold text-amber-500">Προγραμματισμένη αλλαγή πλάνου</p>
                        <p className="text-sm text-gray-300 mt-1 leading-relaxed">
                          Στις <span className="text-white font-bold">{formatGreekDate(subscription.end_date)}</span> το πλάνο σας θα αλλάξει αυτόματα σε <span className="text-white font-bold bg-amber-500/20 px-2 py-0.5 rounded">{scheduledChange.plans?.name || scheduledChange.plan_id}</span>.
                          Εκείνη την ημέρα θα γίνει και η νέα χρέωση.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row items-center gap-4 mt-10 pt-6 border-t border-gray-800">
                    <button
                      onClick={() => setShowChangePlanModal(true)}
                      className="w-full sm:w-auto px-8 py-3 rounded-xl font-bold text-white transition-all hover:scale-[1.02] shadow-lg"
                      style={{ background: 'linear-gradient(135deg, #3B82F6, #2563EB)' }}
                    >
                      Αναβάθμιση / Αλλαγή Πλάνου
                    </button>
                    {!isCancelled && (
                      <button
                        onClick={() => setShowCancelConfirm(true)}
                        className="w-full sm:w-auto px-6 py-3 rounded-xl font-medium transition-all hover:bg-red-500/10 border border-transparent hover:border-red-500/30"
                        style={{ color: '#F87171' }}
                      >
                        Ακύρωση Συνδρομής
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ Tab: Usage ═══ */}
        {activeTab === 'usage' && (
          <div className="space-y-6">
            <div className="rounded-xl border shadow-lg" style={{ backgroundColor: '#111827', borderColor: '#374151' }}>
              <div className="px-6 py-5 border-b flex items-center gap-4" style={{ borderColor: '#374151', background: 'linear-gradient(90deg, rgba(55,65,81,0.3), rgba(31,41,55,0.3))' }}>
                <div className="p-3 rounded-xl" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                  <svg className="w-7 h-7 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Κατανάλωση Πόρων</h2>
                  <p className="text-sm text-gray-400 mt-1">Δείτε τη χρήση των ορίων του πλάνου σας για τον τρέχοντα μήνα.</p>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <UsageCard icon="📅" label="Ραντεβού" current={limits.appointments?.current ?? usage?.appointments_count ?? 0} limit={limits.appointments?.limit} />
                  <UsageCard icon="📧" label="Στάλθηκαν Emails" current={limits.emails?.current ?? usage?.emails_sent ?? 0} limit={limits.emails?.limit} />
                  <UsageCard icon="👥" label="Ενεργοί Χρήστες" current={limits.users?.current ?? 0} limit={limits.users?.limit} />
                </div>

                <div className="mt-8 rounded-xl p-4 flex items-center space-x-3 bg-[#1F2937] border border-gray-700">
                  <div className="p-2 bg-gray-800 rounded-lg">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-300">
                    Οι μετρητές ραντεβού και emails ανανεώνονται και μηδενίζονται την <span className="text-white font-bold bg-gray-800 px-2 py-0.5 rounded ml-1">{getNextResetDate()}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══ Tab: History ═══ */}
        {activeTab === 'history' && (
          <div className="rounded-xl border shadow-lg overflow-hidden" style={{ backgroundColor: '#111827', borderColor: '#374151' }}>
            <div className="px-6 py-5 border-b flex items-center gap-4" style={{ borderColor: '#374151', background: 'linear-gradient(90deg, rgba(55,65,81,0.3), rgba(31,41,55,0.3))' }}>
              <div className="p-3 rounded-xl" style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.2)' }}>
                <svg className="w-7 h-7 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Ιστορικό Συναλλαγών</h2>
                <p className="text-sm text-gray-400 mt-1">Αρχείο πληρωμών και αναβαθμίσεων του λογαριασμού σας.</p>
              </div>
            </div>

            {transactionsLoading ? (
              <div className="flex justify-center py-16">
                <div className="w-10 h-10 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : transactions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                <div className="w-20 h-20 bg-[#1F2937] rounded-full flex items-center justify-center mb-4 border border-gray-700 shadow-inner">
                  <svg className="w-10 h-10 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-lg font-medium text-gray-300">Δεν υπάρχουν συναλλαγές</p>
                <p className="text-sm mt-1">Οι μελλοντικές σας πληρωμές θα εμφανιστούν εδώ.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-[#1F2937]/50 border-b border-gray-700">
                      <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Ημερομηνία</th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Τύπος</th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Περιγραφή</th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Ποσό</th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-center">Κατάσταση</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {transactions.map((tx) => {
                      const typeLabels = { activation: 'Ενεργοποίηση', payment: 'Πληρωμή', cancellation: 'Ακύρωση', plan_change: 'Αλλαγή πλάνου' };
                      const typeColors = { activation: '#10B981', payment: '#3B82F6', cancellation: '#EF4444', plan_change: '#F59E0B' };
                      const statusLabels = { completed: 'Ολοκληρώθηκε', failed: 'Απέτυχε', scheduled: 'Προγραμματισμένο', pending: 'Σε εκκρεμότητα' };
                      const statusColors = { completed: '#10B981', failed: '#EF4444', scheduled: '#F59E0B', pending: '#6B7280' };
                      const color = typeColors[tx.type] || '#6B7280';
                      const stColor = statusColors[tx.status] || '#6B7280';

                      return (
                        <tr key={tx.id} className="hover:bg-[#1F2937]/30 transition-colors">
                          <td className="px-6 py-4 text-sm font-medium text-gray-300 whitespace-nowrap">{formatGreekDate(tx.created_at)}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-3 py-1 text-xs font-bold rounded-md" style={{ backgroundColor: `${color}15`, color, border: `1px solid ${color}30` }}>
                              {typeLabels[tx.type] || tx.type}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-400">{tx.description}</td>
                          <td className="px-6 py-4 text-sm text-right whitespace-nowrap font-bold text-white">
                            {Number(tx.amount) > 0 ? `€${Number(tx.amount).toFixed(2)}` : '—'}
                          </td>
                          <td className="px-6 py-4 text-center whitespace-nowrap">
                            <span className="px-3 py-1 text-xs font-bold rounded-full" style={{ backgroundColor: `${stColor}15`, color: stColor }}>
                              {statusLabels[tx.status] || tx.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>

      {/* ═══ Cancel Confirmation Dialog ═══ */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
          <div className="w-full max-w-md rounded-2xl p-8 shadow-2xl border border-gray-700" style={{ backgroundColor: '#111827' }}>
            <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            
            <h3 className="text-xl font-bold text-white text-center mb-2">Ακύρωση Συνδρομής;</h3>
            <p className="text-gray-400 text-sm text-center mb-8 leading-relaxed">
              Είστε σίγουροι ότι θέλετε να ακυρώσετε; Θα έχετε πλήρη πρόσβαση μέχρι τις <span className="text-white font-bold">{formatGreekDate(subscription?.end_date)}</span>. Μετά τη λήξη, ο λογαριασμός σας θα κλειδωθεί μέχρι να επιλέξετε νέο πλάνο.
            </p>

            <div className="flex flex-col gap-3">
              <button
                onClick={handleCancelSubscription}
                disabled={cancelling}
                className="w-full py-3 rounded-xl font-bold text-white transition-all hover:scale-[1.02] disabled:opacity-60 shadow-lg"
                style={{ background: 'linear-gradient(135deg, #EF4444, #DC2626)' }}
              >
                {cancelling ? 'Ακύρωση...' : 'Ναι, θέλω να ακυρώσω'}
              </button>
              <button
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancelling}
                className="w-full py-3 rounded-xl font-bold text-gray-300 transition-colors border border-gray-600 hover:bg-gray-800"
              >
                Επιστροφή με ασφάλεια
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Change Plan Modal ═══ */}
      {showChangePlanModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 lg:p-8 backdrop-blur-sm" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
          <div className="w-full max-w-6xl max-h-[95vh] overflow-y-auto rounded-2xl shadow-2xl border border-gray-700 custom-scrollbar" style={{ backgroundColor: '#111827' }}>
            
            {/* Modal Header */}
            <div className="sticky top-0 z-20 px-6 py-5 border-b flex items-center justify-between backdrop-blur-md bg-[#111827]/90" style={{ borderColor: '#374151' }}>
              <div>
                <h3 className="text-2xl font-bold text-white">Αλλαγή Πλάνου</h3>
                <p className="text-sm text-gray-400 mt-1">Επιλέξτε το πακέτο που ταιριάζει απόλυτα στις ανάγκες της κλινικής σας.</p>
              </div>
              <button onClick={() => setShowChangePlanModal(false)} className="p-2 rounded-lg text-gray-400 hover:text-white bg-gray-800/50 hover:bg-gray-700 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="p-6 lg:p-8">
              {/* Billing Period Toggle */}
              <div className="flex flex-col items-center justify-center mb-10">
                <div className="inline-flex p-1.5 rounded-xl bg-[#1F2937] border border-gray-700 shadow-inner">
                  <button
                    onClick={() => setBillingPeriod('monthly')}
                    className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all ${
                      billingPeriod === 'monthly' ? 'bg-[#374151] text-white shadow-md' : 'text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    Μηνιαία
                  </button>
                  <button
                    onClick={() => setBillingPeriod('annual')}
                    className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${
                      billingPeriod === 'annual' ? 'bg-emerald-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    Ετήσια
                    <span className={`px-2 py-0.5 text-[10px] uppercase rounded-full ${billingPeriod === 'annual' ? 'bg-white/20 text-white' : 'bg-emerald-500/20 text-emerald-400'}`}>
                      -17%
                    </span>
                  </button>
                </div>
                {billingPeriod === 'monthly' && (
                  <p className="text-sm text-emerald-400 mt-4 font-medium flex items-center gap-1.5 bg-emerald-500/10 px-4 py-1.5 rounded-full border border-emerald-500/20">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                    Εξοικονομήστε 17% με την ετήσια χρέωση (2 Μήνες Δώρο!)
                  </p>
                )}
              </div>

              {/* Plan Cards Grid */}
              {availablePlans.length === 0 ? (
                <div className="rounded-xl p-12 text-center border border-gray-700 bg-[#1F2937]">
                  <p className="text-gray-300 text-lg font-medium">Φορτώνουν τα πλάνα...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
                  {availablePlans.map((planItem) => {
                    const features = getPlanFeatures(planItem);
                    const displayPrice = getPlanPrice(planItem);
                    
                    // Identify the "Professional" or middle plan to highlight it
                    const isHighlighted = planItem.name.toLowerCase().includes('professional') || planItem.name.toLowerCase().includes('premium');
                    const changeType = getChangeType(planItem);
                    const isSame = changeType === 'same';
                    const isCurrentPlan = planItem.id === plan?.id;

                    const buttonLabel = (() => {
                      if (changingPlan) return 'Μεταφορά...';
                      if (isSame) return 'Τρέχον πλάνο';
                      if (changeType === 'period_change') return billingPeriod === 'annual' ? 'Αλλαγή σε Ετήσιο' : 'Αλλαγή σε Μηνιαίο';
                      if (changeType === 'downgrade') return 'Υποβάθμιση';
                      if (changeType === 'upgrade') return 'Αναβάθμιση';
                      return 'Επιλογή';
                    })();

                    return (
                      <div
                        key={planItem.id}
                        className={`relative rounded-3xl p-8 flex flex-col transition-all duration-300 ${
                          isHighlighted 
                            ? 'bg-[#1F2937] border-2 border-emerald-500 shadow-2xl shadow-emerald-500/20 md:-mt-4 md:mb-4 scale-105 z-10' 
                            : 'bg-[#1A202C] border border-gray-700 hover:border-gray-500'
                        }`}
                      >
                        {isHighlighted && (
                          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                            <span className="bg-emerald-500 text-white text-xs font-bold uppercase tracking-widest py-1.5 px-4 rounded-full shadow-lg">
                              Προτεινομενο
                            </span>
                          </div>
                        )}
                        
                        {isCurrentPlan && !isCancelled && !isExpired && !isHighlighted && (
                          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                            <span className="bg-gray-600 text-white text-xs font-bold uppercase tracking-widest py-1.5 px-4 rounded-full shadow-lg">
                              Το Πλανο Σας
                            </span>
                          </div>
                        )}

                        <div className="mb-6 mt-2">
                          <h4 className="text-2xl font-bold text-white mb-2">{planItem.name}</h4>
                          <p className="text-sm text-gray-400 min-h-[40px]">{planItem.description}</p>
                        </div>

                        <div className="mb-8 pb-8 border-b border-gray-700/50">
                          <div className="flex items-end gap-1">
                            <span className="text-4xl font-extrabold text-white">€{displayPrice}</span>
                            <span className="text-gray-400 font-medium mb-1">/{billingPeriod === 'annual' ? 'έτος' : 'μήνα'}</span>
                          </div>
                          {billingPeriod === 'annual' && Number(planItem.price) > 0 && (
                            <p className="text-sm text-emerald-400 mt-2 font-medium bg-emerald-500/10 inline-block px-3 py-1 rounded-md">
                              Mόνο €{(Number(planItem.price)).toFixed(2)} / μήνα
                            </p>
                          )}
                        </div>

                        {/* Limits Highlight Boxes */}
                        <div className="space-y-3 mb-8 bg-[#111827] p-4 rounded-xl border border-gray-800">
                          <div className="flex items-center space-x-3 text-sm font-medium text-white">
                            <span className="p-1.5 bg-blue-500/20 text-blue-400 rounded-md">📅</span>
                            <span>{planItem.max_appointments ? `${planItem.max_appointments} ραντεβού/μήνα` : 'Απεριόριστα ραντεβού'}</span>
                          </div>
                          <div className="flex items-center space-x-3 text-sm font-medium text-white">
                            <span className="p-1.5 bg-purple-500/20 text-purple-400 rounded-md">📧</span>
                            <span>{planItem.max_emails ? `${planItem.max_emails} emails/μήνα` : 'Απεριόριστα emails'}</span>
                          </div>
                          <div className="flex items-center space-x-3 text-sm font-medium text-white">
                            <span className="p-1.5 bg-amber-500/20 text-amber-400 rounded-md">👥</span>
                            <span>{planItem.max_users ? `Έως ${planItem.max_users} χρήστες` : 'Απεριόριστοι χρήστες'}</span>
                          </div>
                        </div>

                        {/* Features List */}
                        <div className="space-y-3 mb-8 flex-1">
                          {features.map((feat, i) => (
                            <div key={i} className="flex items-start space-x-3 text-sm text-gray-300">
                              <svg className="w-5 h-5 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                              <span className="leading-tight">{feat}</span>
                            </div>
                          ))}
                        </div>

                        {/* Action Button */}
                        <button
                          onClick={() => handleChangePlan(planItem.id)}
                          disabled={changingPlan || isSame}
                          className={`w-full py-4 rounded-xl font-bold text-white transition-all duration-200 ${
                            isSame 
                              ? 'bg-[#374151] cursor-not-allowed' 
                              : isHighlighted 
                                ? 'bg-emerald-500 hover:bg-emerald-400 shadow-lg shadow-emerald-500/30 hover:scale-[1.02]' 
                                : 'bg-[#374151] hover:bg-gray-600 hover:scale-[1.02]'
                          }`}
                        >
                          {buttonLabel}
                        </button>
                        
                        {/* Downgrade notice */}
                        {changeType === 'downgrade' && (
                          <p className="text-center text-xs text-amber-500 mt-3 font-medium">
                            Η αλλαγή θα εφαρμοστεί: {formatGreekDate(subscription?.end_date)}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;